import React, { useMemo } from 'react';
import { Row, Col, FormGroup, Label } from 'reactstrap';
import Select from 'components/Select';
import DollarInput from '../DollarInput';
import StatesInput from 'components/StatesInput';
import { useUpdateSalesLocation } from '../../api/sales-locations/service';
import { toNumber } from 'utils/currency';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';

const SalesDefaults = ({ formData, activeColor = "blue", isTheme2 = false, portfolioOptions, salesLocationId, onSave }) => {
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );

    // Safety check - if formData is undefined, provide default values
    const safeFormData = formData || {};
    const updateSalesLocationMutation = useUpdateSalesLocation();

    // Local state for form data
    const [localFormData, setLocalFormData] = React.useState(formData || {});

    // Update local state when formData prop changes
    React.useEffect(() => {
        // Convert API data format to UI format
        const processedData = {
            ...safeFormData,
            // Convert {id: 9} objects to string IDs
            cash_portfolio: safeFormData.cash_portfolio?.id || safeFormData.cash_portfolio || '',
            wholesale_portfolio: safeFormData.wholesale_portfolio?.id || safeFormData.wholesale_portfolio || '',
            // Keep registration_state as object for StatesInput
            registration_state: safeFormData.registration_state?.id ? {
                id: safeFormData.registration_state.id,
                abbreviation: safeFormData.registration_state.abbreviation
            } : safeFormData.registration_state || null
        };
        setLocalFormData(processedData);
    }, [formData]);

    // Use ref to store the latest save function
    const saveSalesDefaultsRef = React.useRef();

    // Function to save sales defaults data
    const saveSalesDefaults = React.useCallback(() => {
        if (salesLocationId && localFormData) {
            const updateData = {
                cash_portfolio: localFormData.cash_portfolio || null,
                wholesale_portfolio: localFormData.wholesale_portfolio || null,
                registration_state: localFormData.registration_state || null,
                dealer_rebates: localFormData.dealer_rebates || false,
                manufacturer_rebates: localFormData.manufacturer_rebates || false,
                pack_fee: toNumber(localFormData.pack_fee) || 0,
                tax_cap: toNumber(localFormData.tax_cap) || 0
            };

            updateSalesLocationMutation.mutate({
                id: salesLocationId,
                data: updateData
            });
        }
    }, [salesLocationId, localFormData, updateSalesLocationMutation]);

    // Update ref whenever saveSalesDefaults changes
    React.useEffect(() => {
        saveSalesDefaultsRef.current = saveSalesDefaults;
    }, [saveSalesDefaults]);

    // Call onSave callback when component mounts to register save function
    React.useEffect(() => {
        if (onSave) {
            onSave('SALES_DEFAULT', () => saveSalesDefaultsRef.current?.());
        }
    }, [onSave]);

    return (
        <div className="">
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label className='form-control-label'>Cash Sales Book to This Portfolio:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.cash_portfolio ? portfolioOptions.find(opt => opt.value === localFormData.cash_portfolio) : null}
                            onChange={(option) => setLocalFormData(prev => ({ ...prev, cash_portfolio: option?.value || '' }))}
                            options={portfolioOptions}
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label className='form-control-label'>Wholesale Sales Book to This Portfolio:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.wholesale_portfolio ? portfolioOptions.find(opt => opt.value === localFormData.wholesale_portfolio) : null}
                            onChange={(option) => setLocalFormData(prev => ({ ...prev, wholesale_portfolio: option?.value || '' }))}
                            options={portfolioOptions}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label className='form-control-label'>Default Contract/Registration State:</Label>
                        <StatesInput
                            name="registration_state"
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.registration_state}
                            onChange={(stateObject) => {
                                setLocalFormData(prev => ({
                                    ...prev,
                                    registration_state: stateObject ? {
                                        id: stateObject.id,
                                        abbreviation: stateObject.abbreviation
                                    } : null
                                }));
                            }}
                            placeholder="Select State"
                        />
                    </FormGroup>
                </Col>
                <Col md="6">
                    <FormGroup>
                        <Label className='form-control-label'>Offer Dealer Rebates:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.dealer_rebates !== undefined ? {
                                value: localFormData.dealer_rebates ? 'yes' : 'no',
                                label: localFormData.dealer_rebates ? 'Yes' : 'No'
                            } : null}
                            onChange={(option) => setLocalFormData(prev => ({ ...prev, dealer_rebates: option?.value === 'yes' }))}
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' }
                            ]}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Offer Manufacturer Rebates:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.manufacturer_rebates !== undefined ? {
                                value: localFormData.manufacturer_rebates ? 'yes' : 'no',
                                label: localFormData.manufacturer_rebates ? 'Yes' : 'No'
                            } : null}
                            onChange={(option) => setLocalFormData(prev => ({ ...prev, manufacturer_rebates: option?.value === 'yes' }))}
                            options={[
                                { value: 'yes', label: 'Yes' },
                                { value: 'no', label: 'No' }
                            ]}
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Default Pack Fee:</Label>
                        <DollarInput
                            value={localFormData.pack_fee}
                            onChange={(value) => setLocalFormData(prev => ({ ...prev, pack_fee: value }))}
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Trade-in Tax Cap:</Label>
                        <DollarInput
                            value={localFormData.tax_cap}
                            onChange={(value) => setLocalFormData(prev => ({ ...prev, tax_cap: value }))}
                        />
                    </FormGroup>
                </Col>

            </Row>
        </div>
    );
};

export default SalesDefaults;
